import React from 'react';
import PropTypes from 'prop-types';
import _maxBy from 'lodash/maxBy';
import _orderBy from 'lodash/orderBy';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { selectBarChartData } from '../../../../utils';
import SplitContentContainer from '../SplitContentContainer/SplitContentContainer';
import SRLStackedRowGraph, { FrequencyLegend, TimesBars, TimeAliases }  from '../SRLStackedRowGraph/SRLStackedRowGraph';
import SRLFrequencyCustomTooltip from '../SRLStackedRowGraph/SRLFrequencyCustomTooltip/SRLFrequencyCustomTooltip';
import { getFormattedDataStackedBarChart } from '../SRLStackedRowGraph/SRLStackedRowGraphUtil';

import s from './SRLHelpAndCollaborationContent.module.css';

const renderHelpBarChart = (chartData, isMobileXS) => {
  let highDomainLimit = 40;
  const maxData = _maxBy(chartData, 'student_count');
  if (maxData) {
    highDomainLimit = Number(maxData.student_count);
  }

  let formattedChartData = [];
  if (Array.isArray(chartData)) {
    formattedChartData = chartData.map((barData) => ({
        answer: barData.display_text || barData.answer,
        student_count: parseInt(barData.student_count, 10),
      }));
    formattedChartData = _orderBy(formattedChartData, ['student_count'], ['desc']);
  }
  const height = isMobileXS ? 242 : '100%';
  const graph = (
      <ResponsiveContainer height={height}>
        <BarChart
          data={formattedChartData}
          margin={{ top: 0, right: 100, left: 100, bottom: 10 }}
          layout="vertical"
          barCategoryGap="20%"
        >
          <Bar
            dataKey="student_count"
            fill="var(--prs-c-info-800)"
            minPointSize={0}
            label={{
              value: 'student_count',
              position: 'right',
              fontFamily: 'Source Sans Pro',
              fontSize: 14,
              fill: '#242424' }}
          />
          <YAxis
            dataKey="answer"
            type="category"
            tick={{
              fill: '#242424',
              fontSize: 14,
              width: 160,
            }}
            tickLine={false}
            interval={0}
          />
          <XAxis
            tick={false}
            axisLine={false}
            tickLine={false}
            domain={[0, highDomainLimit]}
            type="number"
            label={{ value: 'total students',
              position: 'center',
              fontSize: 14,
              fontFamily: 'Source Sans Pro',
              fontStyle: 'italic',
              fill: '#242424',
            }}
          />
          <CartesianGrid vertical={false} horizontal={false} />
        </BarChart>
      </ResponsiveContainer>
  );
  // if is a mobile xs resolution need to include a wrapper
  return !isMobileXS ? graph : <div className={s.fixedHeight}>{graph}</div>;
};


const renderHelpStackedBarChart = (chartData, isMobileXS) => {
  const formattedChartData = getFormattedDataStackedBarChart(chartData, TimeAliases);
  return (
    <div className={isMobileXS ? s.fixedHeight : ''}>
      <SRLStackedRowGraph
        yAxisWidth={250}
        chartData={formattedChartData}
        height={242}
        legendComponent={FrequencyLegend}
        bars={TimesBars}
        tooltipContent={SRLFrequencyCustomTooltip}
      />
    </div>
  );
};

function SRLHelpAndCollaborationContent({ data, isMobileXS }) {
  const { insightsData, isSRLIntroSurvey } = data;

  const rightColumnContent = null;
  let leftColumnContent = null;
  if (isSRLIntroSurvey) {
    const chartData = selectBarChartData(insightsData);
    leftColumnContent = renderHelpBarChart(chartData, isMobileXS);
  } else {
    leftColumnContent = renderHelpStackedBarChart(insightsData, isMobileXS);
  }

  return (
    <SplitContentContainer
      leftColumnContent={leftColumnContent}
      rightColumnContent={rightColumnContent}
      borderPosition="left"
    />
  );
}

SRLHelpAndCollaborationContent.propTypes = {
  data: PropTypes.shape({
    insightsData: PropTypes.array.isRequired,
    isSRLIntroSurvey: PropTypes.bool,
  }),
  isMobileXS: PropTypes.bool,
};

export default SRLHelpAndCollaborationContent;
